/**
 * PlannerFocusPanel — what a discharge planner needs to ACTION today.
 *
 * Order matters (top-left → bottom-right reading flow): the most
 * time-sensitive items appear first.
 *   1. Referrals awaiting review (their inbox)
 *   2. Urgent · discharging in 48h (their fires)
 *   3. Placements pending provider accept (their bottleneck)
 *   4. First-visit windows at risk (their SLA promises)
 *
 * Each row deep-links to the existing planner detail surfaces — no
 * routes were renamed, no new screens needed.
 */
import React from 'react';
import { AlertTriangle, MapPin, CalendarClock, Stethoscope } from 'lucide-react';
import { FocusCard, FocusHero } from './FocusCard';

export function PlannerFocusPanel({ focus }) {
  const blocks = focus?.blocks || [];
  const byKey = Object.fromEntries(blocks.map((b) => [b.key, b]));

  return (
    <div className="space-y-6" data-testid="planner-focus-panel">
      <FocusHero
        title="Discharge today, in care tomorrow."
        subtitle="The three or four things that need you right now — nothing else, until you're ready."
        primaryAction={focus?.primary_action}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FocusCard
          block={byKey.referrals_awaiting_review}
          testIdPrefix="planner-focus"
          emptyHint="No referrals waiting on review."
          renderRow={(r) => (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">
                  {r.patient_initials || '—'}
                  {r.urgency === 'urgent' && (
                    <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500 text-white inline-flex items-center gap-1">
                      <AlertTriangle className="w-2.5 h-2.5" /> Urgent
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-2 mt-0.5">
                  {r.care_type && (
                    <span className="inline-flex items-center gap-1">
                      <Stethoscope className="w-3 h-3" /> {r.care_type}
                    </span>
                  )}
                  {r.discharge_date && (
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="w-3 h-3" /> {r.discharge_date}
                    </span>
                  )}
                </div>
              </div>
              <a href={`/planner/dashboard`}
                  className="text-xs text-slate-600 hover:text-slate-900">
                Open →
              </a>
            </>
          )}
        />

        <FocusCard
          block={byKey.referrals_needing_attention}
          testIdPrefix="planner-focus"
          emptyHint="No urgent referrals in the next 48 hours."
          renderRow={(r) => (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">
                  {r.patient_initials || '—'}
                  {' '}
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500 text-white">
                    {r.status || '—'}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Discharges {r.discharge_date}
                </div>
              </div>
              <a href={`/planner/dashboard`}
                  className="text-xs text-slate-600 hover:text-slate-900">
                Open →
              </a>
            </>
          )}
        />

        <FocusCard
          block={byKey.placements_pending_accept}
          testIdPrefix="planner-focus"
          emptyHint="No placements waiting on a provider."
          renderRow={(r) => (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">
                  {r.patient_initials || r.patient_id?.slice(0, 8) || '—'}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Offered {r.offered_at ? new Date(r.offered_at).toLocaleString() : '—'}
                </div>
              </div>
              <a href={`/planner/dashboard`}
                  className="text-xs text-slate-600 hover:text-slate-900">
                Open →
              </a>
            </>
          )}
        />

        <FocusCard
          block={byKey.first_visit_at_risk}
          testIdPrefix="planner-focus"
          emptyHint="Every first-visit window is on track."
          renderRow={(r) => (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">
                  {r.patient_initials || '—'}
                </div>
                <div className="text-xs text-rose-700 mt-0.5">
                  {r.hours_since_accept || 0}h since accept · no visit scheduled
                </div>
              </div>
              <a href={`/planner/dashboard`}
                  className="text-xs text-slate-600 hover:text-slate-900">
                Nudge →
              </a>
            </>
          )}
        />
      </div>
    </div>
  );
}
